/**
 * TaskListView Component
 * Table view for displaying tasks grouped by status with infinite scroll per group
 */
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { reorderStatuses, updateStatusOrder, updateTicketPartial, optimisticMergeTicket } from '../../store/ticketSlice';
import { toast } from 'react-toastify';
import { USER_ROLES } from '../../shared/appConstants';
import AssigneeAvatarStack from '../../shared/AssigneeAvatarStack';
import { ICONS } from '../../shared/icons';
import { isDueDateOverdue } from '../../shared/timeUtils';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
  Box,
  Tooltip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  MenuItem,
  Divider
} from '@mui/material';
import {
  Schedule,
  Flag,
  KeyboardArrowDown,
  ExpandMore,
  DragIndicator,
  MoreVert,
  CheckBox,
  CheckBoxOutlineBlank,
  HourglassEmpty,
  PushPin,
  InfoOutlined,
  ContentCopy,
} from '@mui/icons-material';
import { COLORS, UI } from '../../shared/colors';
import { handleCopyTaskLink, handleCopyTaskTitle } from '../../shared/taskUtils';
import { tableHeaderStyles, taskListStyles, smallIconButtonStyles } from '../../shared/globalSX';
import { createDragImage, createAutoScroll, createAccordionDragManager, createStatusDragHandlers, cleanupDragVisuals } from '../../shared/dragUtils';
import InlineSubtaskRow from './InlineSubtaskRow';
import { getStatusIcon } from '../../shared/iconUtils';
import TaskCompletionCheckbox from './TaskCompletionCheckbox';
import LabelChips from './LabelChips';
import KebabMenu, { useMenuState } from '../shared/KebabMenu';
import { BULK_ACTION_ITEMS } from '../../shared/appConstants';
import OverflowTooltip from '../../shared/OverflowTooltip';
import '../../shared/globalStyles.scss';
import './TaskDashboard.scss';

const formatDateOnlyForList = (value, fallback) => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  // List view keeps the cell compact; the full deadline remains available in the tooltip.
  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear().toString().slice(-2);
  return `${day} ${month} ${year}`;
};

const TaskListView = ({
  tasks,
  loadingTaskId,
  selectedProject,
  searchTerm,
  selectedPriority,
  selectedStatus,
  selectedAssignee,
  selectedTaskType,
  dispatch,
  fetchTicketsByProject,
  handleViewTask,
  handleEdit,
  handleDelete,
  getStatusLabel,
  getStatusColor,
  getPriorityColor,
  getDisplayName,
  FILTER_VALUES,
  loadMoreColumnData,
  onEditColumn,
  onDeleteColumn,
  // Drag and drop props
  draggedTask,
  dragOverColumn,
  handleDragStart,
  handleDragEnd,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  // Status drag and drop props
  draggedStatus,
  handleStatusDragStart,
  handleStatusDragEnd,
  performStatusReorder,
  // Multiselect props
  selectedTasksByColumn = {},
  selectedTaskSets = {},
  bulkModeColumnId = null,
  isBulkMode = false,
  isSelectionLimitReached = false,
  onTaskSelect,
  onBulkEdit,
  onBulkDelete,
  onEnterBulkMode,
  onExitBulkMode,
  onSelectAllInColumn,
  onDeselectAllInColumn,
  selectAllModeByColumn = {},
  tasksByStatusMap,
  // Link actions
  onViewLinks,
  onAddLink,
  newlyCreatedTaskId = null,
  t
}) => {
  const { dropdowns, tickets, columnPagination } = useSelector(state => state.tickets);
  const { user } = useSelector(state => state.auth);
  const localDispatch = useDispatch();

  // Permission check for status reordering (Admin/PM only)
  const canReorderStatus = user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.PM;
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedStatuses, setExpandedStatuses] = useState(new Set());
  const kebabMenu = useMenuState();

  // Close kebab menu when bulk mode is exited
  useEffect(() => {
    if (!isBulkMode) {
      kebabMenu.handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBulkMode]);

  const [taskMenuAnchorEl, setTaskMenuAnchorEl] = useState(null);
  const [activeMenuTask, setActiveMenuTask] = useState(null);

  const handleTaskMenuOpen = (e, task) => {
    e.stopPropagation();
    setTaskMenuAnchorEl(e.currentTarget);
    setActiveMenuTask(task);
  };

  const handleTaskMenuClose = (e) => {
    if (e) e.stopPropagation();
    setTaskMenuAnchorEl(null);
    setActiveMenuTask(null);
  };

  const handleTogglePin = useCallback((task) => {
    if (!task?.id) return;
    const ticketLabel = task.ticket_number || task.code || task.id;
    const nextPinned = !task.marked_as_pin;

    localDispatch(optimisticMergeTicket({
      ticketId: task.id,
      updates: { marked_as_pin: nextPinned }
    }));

    dispatch(updateTicketPartial({
      ticketId: task.id,
      ticketData: {
        marked_as_pin: nextPinned
      }
    })).unwrap()
      .then(() => {
        toast.success(nextPinned
          ? t('dashboard.ticketPinnedSuccess', '{{ticketLabel}} pinned successfully', { ticketLabel })
          : t('dashboard.ticketUnpinnedSuccess', '{{ticketLabel}} unpinned successfully', { ticketLabel }));
      })
      .catch((error) => {
        localDispatch(optimisticMergeTicket({
          ticketId: task.id,
          updates: { marked_as_pin: task.marked_as_pin }
        }));
        console.error('Failed to toggle ticket pin state:', error);
        toast.error(t('dashboard.ticketPinUpdateFailed', 'Failed to update ticket pin status'));
      });
  }, [dispatch, localDispatch, t]);

  const statusScrollRefs = useRef({});
  const accordionSummaryRefs = useRef({});
  const dragOverStatusRef = useRef(null);

  // Callback ref to avoid setting refs on every render
  const setStatusRef = useCallback((el, statusId) => {
    if (el) {
      statusScrollRefs.current[statusId] = el;
    }
  }, []);

  const setAccordionSummaryRef = useCallback((el, statusId) => {
    if (el) {
      accordionSummaryRefs.current[statusId] = el;
    }
  }, []);

  // Use tasksByStatusMap from props if available, otherwise create local one
  const localTasksByStatusMap = useMemo(() => {
    if (tasksByStatusMap) return tasksByStatusMap;
    const map = new Map();
    const safeTasks = tasks || [];
    safeTasks.forEach(task => {
      if (!map.has(task.status)) map.set(task.status, []);
      map.get(task.status).push(task);
    });
    return map;
  }, [tasks, tasksByStatusMap]);

  // Group tasks by status
  const tasksByStatus = useMemo(() => {
    const grouped = {};
    const safeStatuses = dropdowns?.statuses || [];
    const safeTickets = tickets || {};

    safeStatuses.forEach(status => {
      const statusData = safeTickets[status?.name] || { total: 0 };
      grouped[status.id] = {
        status,
        tasks: localTasksByStatusMap.get(status.id) || [],
        total: statusData.total || 0
      };
    });
    return grouped;
  }, [localTasksByStatusMap, dropdowns.statuses, tickets]);

  // Memoized overdue calculations
  const overdueMap = useMemo(() => {
    const safeTasks = tasks || [];

    return safeTasks.reduce((acc, task) => {
      if (!task?.id) return acc;

      if (!task.dueDateRaw || task.dueDate === t('dashboard.noDueDate') || task.completed) {
        acc[task.id] = false;
      } else {
        acc[task.id] = isDueDateOverdue(task.dueDateRaw, task.hasDueTime, task.completed);
      }
      return acc;
    }, {});
  }, [tasks, t]);

  const toggleRowExpansion = useCallback((taskId) => {
    setExpandedRows(prev => {
      const newExpandedRows = new Set(prev);
      if (newExpandedRows.has(taskId)) {
        newExpandedRows.delete(taskId);
      } else {
        newExpandedRows.add(taskId);
      }
      return newExpandedRows;
    });
  }, []);

  const toggleStatusExpansion = (statusId) => {
    const newExpandedStatuses = new Set(expandedStatuses);
    if (newExpandedStatuses.has(statusId)) {
      newExpandedStatuses.delete(statusId);
    } else {
      newExpandedStatuses.add(statusId);
    }
    setExpandedStatuses(newExpandedStatuses);
  };

  const copyTaskLink = useCallback((task) => handleCopyTaskLink(task, t), [t]);
  const copyTaskTitle = useCallback((task) => handleCopyTaskTitle(task, t), [t]);

  const handleCopy = useCallback((e, task) => {
    e.stopPropagation();
    copyTaskLink(task);
  }, [copyTaskLink]);

  const handleEditClick = useCallback((e, task) => {
    e.stopPropagation();
    handleEdit(task);
  }, [handleEdit]);

  const handleDeleteClick = useCallback((e, task) => {
    e.stopPropagation();
    handleDelete(task);
  }, [handleDelete]);

  const handleTaskSelect = useCallback((taskId) => {
    onTaskSelect?.(taskId);
  }, [onTaskSelect]);



  const handleKebabMenuItemClick = useCallback((item) => {
    switch (item.action) {
      case 'edit':
        if (onEditColumn) {
          const status = dropdowns.statuses.find(s => s.id === kebabMenu.menuId);
          if (status) onEditColumn({ ...status, title: status.name });
        }
        break;
      case 'delete':
        if (onDeleteColumn) {
          const status = dropdowns.statuses.find(s => s.id === kebabMenu.menuId);
          if (status) onDeleteColumn({ ...status, title: status.name });
        }
        break;
      case 'bulk':
        if (kebabMenu.menuId) {
          // Expand the accordion if it's not already expanded
          if (!expandedStatuses.has(kebabMenu.menuId)) {
            setExpandedStatuses(prev => new Set([...prev, kebabMenu.menuId]));
          }
          onEnterBulkMode?.(kebabMenu.menuId);
        }
        break;
      case 'bulk-edit':
        onBulkEdit?.();
        break;
      case 'bulk-delete':
        onBulkDelete?.();
        break;
      case 'bulk-exit':
        onExitBulkMode?.();
        break;
      default:
        break;
    }
  }, [kebabMenu.menuId, expandedStatuses, onEnterBulkMode, onBulkEdit, onBulkDelete, onExitBulkMode]);

  const getKebabMenuItems = useMemo(() => {
    const status = dropdowns.statuses.find(s => s.id === kebabMenu.menuId);
    const items = [];

    const isColumnInBulkMode = bulkModeColumnId === kebabMenu.menuId;
    const selectedCount = selectedTasksByColumn[kebabMenu.menuId]?.size || 0;

    if (status && status.status !== 'DEFAULT') {
      items.push({ ...BULK_ACTION_ITEMS.EDIT, disabled: isColumnInBulkMode });
      items.push({ ...BULK_ACTION_ITEMS.DELETE, disabled: isColumnInBulkMode });
    }

    items.push({
      ...BULK_ACTION_ITEMS.BULK_ACTIONS,
      id: 'bulk-actions-expander',
      labelNode: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {t('dashboard.bulkActions.actions')}
          {isColumnInBulkMode && selectedCount > 0 && (
            <span className="kebab-bulk-count">{selectedCount}</span>
          )}
        </span>
      ),
      label: 'dashboard.bulkActions.actions',
      stayOpen: true,
      isSelected: isColumnInBulkMode,
      disabled: !tasksByStatus[kebabMenu.menuId]?.tasks?.length,
      children: [
        {
          id: 'bulk-edit',
          label: 'dashboard.bulkActions.edit',
          action: 'bulk-edit',
          color: 'blue',
          stayOpen: true,
          disabled: !selectedTasksByColumn[kebabMenu.menuId]?.size
        },
        {
          id: 'bulk-delete',
          label: 'dashboard.bulkActions.delete',
          action: 'bulk-delete',
          color: 'red',
          stayOpen: true,
          disabled: !selectedTasksByColumn[kebabMenu.menuId]?.size,
          hidden: user?.role === USER_ROLES.USER
        },
        {
          id: 'bulk-exit',
          label: 'dashboard.bulkActions.exitMode',
          action: 'bulk-exit',
          sx: {
            color: '#d32f2f',
            fontWeight: 400,
            '&:hover': { color: '#b71c1c', bgcolor: '#ffebee' }
          }
        }
      ].filter(c => !c.hidden)
    });

    return items;
  }, [kebabMenu.menuId, dropdowns.statuses, tasksByStatus, selectedTasksByColumn, bulkModeColumnId, user?.role, t]);



  // Status drag handlers using centralized utilities
  const statusDragHandlers = useMemo(() => createStatusDragHandlers({
    draggedStatus,
    canReorderStatus,
    statusEntries: Object.entries(tasksByStatus),
    statuses: dropdowns.statuses,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
    dragOverStatusRef,
    dispatch: localDispatch,
    selectedProject,
    t,
    reorderStatuses,
    updateStatusOrder,
    toast,
    performStatusReorder
  }), [draggedStatus, canReorderStatus, dropdowns.statuses, tasksByStatus, handleDragOver, handleDragLeave, handleDrop, localDispatch, selectedProject, t, performStatusReorder]);

  // Auto-scroll handlers - memoized to prevent recreation
  const onContainerDragOver = useMemo(() => createAutoScroll(100, 20), []);
  const onContentDragOver = useMemo(() => createAutoScroll(50, 15), []);

  // Accordion drag manager for visual feedback
  const accordionDragManager = useMemo(() => createAccordionDragManager(expandedStatuses), [expandedStatuses]);

  // Ref-based ticket drag handlers to prevent re-renders
  const localDragOverRef = useRef(null);

  const handleTicketDragOver = useCallback((e, statusId) => {
    if (draggedStatus) return;

    e.preventDefault();
    if (draggedTask && localDragOverRef.current !== statusId) {
      localDragOverRef.current = statusId;
      accordionDragManager.applyTicketDragFeedback(statusId);
    }
    handleDragOver(e, statusId);
    onContentDragOver(e, statusId);
  }, [draggedStatus, draggedTask, handleDragOver, onContentDragOver, accordionDragManager]);

  const handleTicketDragLeave = useCallback((e) => {
    if (draggedStatus) return;

    if (!e.currentTarget.contains(e.relatedTarget)) {
      localDragOverRef.current = null;
      accordionDragManager.clearTicketDragFeedback();
    }
    handleDragLeave(e);
  }, [draggedStatus, handleDragLeave, accordionDragManager]);

  const handleTicketDrop = useCallback((e, statusId) => {
    if (draggedStatus) return;

    localDragOverRef.current = null;
    accordionDragManager.clearTicketDragFeedback();
    handleDrop(e, statusId);
  }, [draggedStatus, handleDrop, accordionDragManager]);



  // Setup scroll listeners for status sections
  useEffect(() => {
    const handleStatusScroll = (statusId) => (e) => {
      const element = e.target;
      const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 5;

      if (isAtBottom) {
        loadMoreColumnData(statusId);
      }
    };

    const scrollListeners = {};

    Object.keys(tasksByStatus).forEach(statusId => {
      const element = statusScrollRefs.current[statusId];
      if (element && !scrollListeners[statusId]) {
        const listener = handleStatusScroll(statusId);
        element.addEventListener('scroll', listener);
        scrollListeners[statusId] = { element, listener };
      }
    });

    return () => {
      Object.values(scrollListeners).forEach(({ element, listener }) => {
        element.removeEventListener('scroll', listener);
      });
    };
  }, [tasksByStatus, loadMoreColumnData]);

  // Auto-select newly loaded tasks when select all mode is active
  useEffect(() => {
    if (!isBulkMode || !bulkModeColumnId || !localTasksByStatusMap) return;

    const isSelectAllActive = selectAllModeByColumn?.[bulkModeColumnId];
    if (!isSelectAllActive) return;

    const columnTasks = localTasksByStatusMap.get(bulkModeColumnId) || [];
    const currentSelected = selectedTasksByColumn[bulkModeColumnId] || new Set();
    const remainingSlots = 100 - currentSelected.size;

    if (remainingSlots <= 0) return;

    // Find tasks that are not yet selected
    const unselectedTasks = columnTasks.filter(task => !currentSelected.has(task.id));

    if (unselectedTasks.length > 0) {
      const tasksToSelect = unselectedTasks.slice(0, remainingSlots);

      // Batch select all new tasks to avoid multiple re-renders
      tasksToSelect.forEach(task => onTaskSelect?.(task.id));
    }
  }, [localTasksByStatusMap, bulkModeColumnId, selectedTasksByColumn, selectAllModeByColumn, isBulkMode, onTaskSelect]);



  // Memoized task row handlers to prevent re-renders
  const createTaskRowHandlers = useCallback((taskId, isInBulkMode) => ({
    onTaskSelect: () => handleTaskSelect(taskId),
    onTaskView: (task) => {
      if (!isInBulkMode && loadingTaskId !== taskId) {
        handleViewTask(task);
      }
    },
    onTaskEdit: (e, task) => handleEditClick(e, task),
    onTaskDelete: (e, task) => handleDeleteClick(e, task),
    onTaskCopy: (e, task) => handleCopy(e, task)
  }), [handleTaskSelect, loadingTaskId, handleViewTask, handleEditClick, handleDeleteClick, handleCopy]);

  const renderTaskRow = useCallback((task) => {
    const isOverdue = overdueMap[task.id];
    const isDragging = draggedTask?.id === task.id;
    const isInBulkMode = bulkModeColumnId === task.status;
    const handlers = createTaskRowHandlers(task.id, isInBulkMode);

    return (
      <React.Fragment key={task.id}>
        <TableRow
          draggable={!isInBulkMode}
          onDragStart={!isInBulkMode ? (e) => {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', task.id);

            // Create and set custom drag image
            const dragElement = createDragImage(e.currentTarget, e.nativeEvent.offsetX, e.nativeEvent.offsetY);
            e.dataTransfer.setDragImage(dragElement, e.nativeEvent.offsetX, e.nativeEvent.offsetY);
            requestAnimationFrame(() => {
              if (dragElement && dragElement.parentNode === document.body) {
                document.body.removeChild(dragElement);
              }
            });

            handleDragStart(e, task);
          } : undefined}
          onDragEnd={!isInBulkMode ? handleDragEnd : undefined}
          onClick={isInBulkMode ? handlers.onTaskSelect : (e) => {
            if (!e.defaultPrevented) {
              handlers.onTaskView(task);
            }
          }}
          sx={{
            ...taskListStyles.taskRow(isOverdue, loadingTaskId, task.id),
            opacity: (!isInBulkMode && isDragging) ? 0.3 : 1,
            filter: (!isInBulkMode && isDragging) ? 'grayscale(50%)' : 'none',

            cursor: isInBulkMode ? 'pointer' : (isDragging ? 'grabbing' : 'grab'),
            transition: `all ${UI.animationMs.highlight}ms ease-in-out`,
            bgcolor: newlyCreatedTaskId === task.id ? UI.newTaskHighlight : (selectedTaskSets[bulkModeColumnId]?.has(task.id) ? COLORS.blue[50] : 'transparent'),
            border: newlyCreatedTaskId === task.id ? `2px solid ${COLORS.primary}` : (selectedTaskSets[bulkModeColumnId]?.has(task.id) ? `1px solid ${COLORS.primary}` : 'none'),
            boxShadow: newlyCreatedTaskId === task.id ? `0 0 0 3px rgba(37, 99, 235, ${UI.shadowOpacity.medium})` : (selectedTaskSets[bulkModeColumnId]?.has(task.id) ? `0 2px 8px rgba(59, 130, 246, ${UI.shadowOpacity.light})` : `0 1px 3px rgba(0, 0, 0, ${UI.shadowOpacity.subtle})`),
            transform: ((!isInBulkMode && isDragging) ? 'scale(0.98)' : 'scale(1)'),
            '&:hover': {
              bgcolor: newlyCreatedTaskId === task.id ? UI.newTaskHighlightHover : (selectedTaskSets[bulkModeColumnId]?.has(task.id) ? COLORS.blue[100] : (isDragging ? 'transparent' : COLORS.gray[50]))
            }
          }}
        >
          <TableCell sx={{ width: '25%', maxWidth: '300px', ...taskListStyles.taskCell }}>
            <Box display="flex" alignItems="center" gap={1}>
              {isInBulkMode && (
                <Tooltip title={selectedTaskSets[bulkModeColumnId]?.has(task.id) ? t('dashboard.bulkActions.deselectTask') : (selectedTasksByColumn[bulkModeColumnId]?.size >= 100 ? t('dashboard.bulkActions.selectionLimitReached') : t('dashboard.bulkActions.selectTask'))} arrow slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlers.onTaskSelect();
                      }}
                      className="task-select-btn"
                      aria-label={selectedTaskSets[bulkModeColumnId]?.has(task.id) ? t('dashboard.bulkActions.deselectTask') : t('dashboard.bulkActions.selectTask')}
                      disabled={!selectedTaskSets[bulkModeColumnId]?.has(task.id) && selectedTasksByColumn[bulkModeColumnId]?.size >= 100}
                    >
                      {selectedTaskSets[bulkModeColumnId]?.has(task.id) ? (
                        <CheckBox className="task-checkbox-selected" />
                      ) : (
                        <CheckBoxOutlineBlank className="task-checkbox-unselected" sx={{ opacity: (!selectedTaskSets[bulkModeColumnId]?.has(task.id) && selectedTasksByColumn[bulkModeColumnId]?.size >= 100) ? 0.5 : 1 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {!isInBulkMode && (
                <TaskCompletionCheckbox task={task} size="small" disabled={loadingTaskId === task.id} />
              )}
              {loadingTaskId === task.id && <CircularProgress size={18} />}
              {task.sub_task_count > 0 && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="caption" sx={taskListStyles.subtaskCount}>
                    {task.sub_task_count}
                  </Typography>
                  <IconButton size="small" onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleRowExpansion(task.id); }}>
                    <KeyboardArrowDown sx={{ ...taskListStyles.expandIcon, transform: expandedRows.has(task.id) ? 'rotate(180deg)' : 'none' }} />
                  </IconButton>
                </Box>
              )}
              {task.marked_as_pin && (
                <Tooltip title={t('dashboard.pinnedTicket', 'Pinned ticket')} arrow slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                  <PushPin sx={{ fontSize: 13, color: COLORS.primary, transform: 'rotate(35deg)', flexShrink: 0 }} />
                </Tooltip>
              )}
              {newlyCreatedTaskId === task.id && (
                <Chip
                  label="NEW"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '9px',
                    fontWeight: 700,
                    bgcolor: COLORS.primary,
                    color: COLORS.white,
                    '& .MuiChip-label': { px: 0.75 },
                    flexShrink: 0
                  }}
                />
              )}
              <OverflowTooltip title={task.title || 'NA'} variant="body2" fontWeight="500" tooltipWidth={350} placement="top-start" />
              {isOverdue && (
                <Chip
                  icon={<Schedule sx={{ fontSize: '11px !important', color: `${COLORS.red[700]} !important` }} />}
                  label={t('dashboard.taskIsOverdue', 'Overdue')}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '9px',
                    fontWeight: 600,
                    bgcolor: COLORS.red[100],
                    color: COLORS.red[700],
                    border: `1px solid ${COLORS.red[200]}`,
                    '& .MuiChip-label': { px: 0.75 },
                    '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
                    flexShrink: 0
                  }}
                />
              )}
            </Box>
          </TableCell>
          <TableCell sx={{ width: '12%', ...taskListStyles.taskCell }}>
            <Chip label={task.code || 'NA'} size="small" sx={taskListStyles.codeChip} />
          </TableCell>
          <TableCell sx={{ width: '15%', ...taskListStyles.taskCell }}>
            <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0, width: '100%' }}>
              {task.priority ? (
                <>
                  <Flag sx={{ ...taskListStyles.iconSmall, color: getPriorityColor(task.priority, { priorities: dropdowns.priorities }), flexShrink: 0 }} />
                  <OverflowTooltip
                    title={getDisplayName(task.priority, 'priority')}
                    tooltipWidth={200}
                    variant="body2"
                    sx={{ ...taskListStyles.priorityText, minWidth: 0, flex: 1, width: '100%' }}
                    placement="top-start"
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">NA</Typography>
              )}
            </Box>
          </TableCell>
          <TableCell sx={{ width: '18%', ...taskListStyles.taskCell }}>
            <Box display="flex" alignItems="center">
              {((Array.isArray(task.assignees) && task.assignees.length > 0) || task.assignee) ? (
                <AssigneeAvatarStack assignees={task.assignees} assignee={task.assignee} />
              ) : (
                <Typography variant="body2" color="text.secondary">NA</Typography>
              )}
            </Box>
          </TableCell>
          <TableCell sx={{ width: '8%', ...taskListStyles.taskCell }}>
            {task.labels && task.labels.length > 0 ? (
              <LabelChips labels={task.labels} />
            ) : (
              <Typography variant="body2" color="text.secondary">NA</Typography>
            )}
          </TableCell>
          <TableCell sx={{ width: '10%', ...taskListStyles.taskCell }}>
            {task.estimateTime ? (
              <Box display="flex" alignItems="center" gap={0.5}>
                <HourglassEmpty sx={{ fontSize: '12px', color: COLORS.gray[400] }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: COLORS.gray[600],
                    fontSize: '14px',
                    fontWeight: 500,
                    '@media (max-width: 1380px)': {
                      fontSize: '13px'
                    }
                  }}
                >
                  {task.estimateTime}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">NA</Typography>
            )}
          </TableCell>
          <TableCell sx={{ width: '10%', ...taskListStyles.taskCell }}>
            {(task.dueDate && task.dueDate !== t('dashboard.noDueDate')) ? (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Schedule sx={{ ...taskListStyles.iconSmall, color: isOverdue ? COLORS.red[500] : COLORS.gray[400] }} />
                <Tooltip title={task.dueDateTooltip || task.dueDate} arrow slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                  <Typography
                    variant="body2"
                    sx={{
                      ...taskListStyles.dueDateText,
                      color: isOverdue ? COLORS.red[600] : COLORS.gray[500],
                      fontWeight: isOverdue ? 600 : 500
                    }}
                  >
                    {formatDateOnlyForList(task.dueDateRaw, task.dueDate)}
                  </Typography>
                </Tooltip>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">NA</Typography>
            )}
          </TableCell>
          <TableCell sx={{ width: '10%', ...taskListStyles.taskCell }}>
            {!isInBulkMode && (
              <IconButton
                size="small"
                onClick={(e) => handleTaskMenuOpen(e, task)}
                sx={{ ...smallIconButtonStyles, color: COLORS.gray[500] }}
              >
                <MoreVert sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </TableCell>
        </TableRow>
        {expandedRows.has(task.id) && (
          <TableRow>
            <TableCell sx={taskListStyles.expandedCell} colSpan={8}>
              <InlineSubtaskRow task={task} isExpanded={expandedRows.has(task.id)} onToggle={() => toggleRowExpansion(task.id)} />
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  }, [overdueMap, draggedTask, bulkModeColumnId, selectedTaskSets, selectedTasksByColumn, createTaskRowHandlers, handleDragStart, handleDragEnd, t, expandedRows, toggleRowExpansion, getDisplayName, getPriorityColor, loadingTaskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const statusEntries = Object.entries(tasksByStatus);

  // Always clean up when drag ends (works for both valid and invalid drops)
  React.useEffect(() => {
    const handleDragEnd = () => cleanupDragVisuals(dragOverStatusRef);

    document.addEventListener('dragend', handleDragEnd);
    return () => document.removeEventListener('dragend', handleDragEnd);
  }, []);

  return (
    <>

      <Box
        className={`tickets-container-list ${isBulkMode ? 'bulk-mode' : ''} ${draggedStatus && dragOverStatusRef.current?.position === 'top' ? 'drag-status-top' : ''}`}
        sx={{
          ...taskListStyles.container,
          ...(isBulkMode && {
            pb: 8
          })
        }}
        onDragOver={onContainerDragOver}
        onDrop={(e) => {
          if (!draggedStatus) return;

          e.preventDefault();

          // Find closest accordion using refs instead of document query
          let closestStatusId = null;
          let minDistance = Infinity;

          Object.entries(accordionSummaryRefs.current).forEach(([statusId, element]) => {
            if (element) {
              const rect = element.getBoundingClientRect();
              const distance = Math.abs(e.clientY - (rect.top + rect.height / 2));
              if (distance < minDistance) {
                minDistance = distance;
                closestStatusId = statusId;
              }
            }
          });

          // Only drop if we found a valid closest element and it's within reasonable distance
          if (closestStatusId && minDistance < 200) {
            statusDragHandlers.drop(e, closestStatusId);
          } else {
            cleanupDragVisuals(dragOverStatusRef);
          }
        }}
      >
        {/* Top boundary indicator */}
        {draggedStatus && dragOverStatusRef.current?.position === 'top' && dragOverStatusRef.current?.targetIndex !== null && (
          <Box
            key={`top-${dragOverStatusRef.current?.targetIndex}-${dragOverStatusRef.current?.isGhost}`}
            className={dragOverStatusRef.current?.isGhost ? "status-drop-indicator-ghost" : "status-drop-indicator"}
            sx={{ mb: 0.5, ml: 'auto' }}
          />
        )}

        {statusEntries.map(([statusId, { status, tasks: statusTasks, total }], index) => (
          <React.Fragment key={statusId}>
            {/* Drop indicator before current item */}
            {draggedStatus && dragOverStatusRef.current?.position === 'between' && dragOverStatusRef.current?.targetIndex === index && dragOverStatusRef.current?.targetIndex !== null && (
              <Box
                key={`between-${index}-${dragOverStatusRef.current?.isGhost}`}
                className={dragOverStatusRef.current?.isGhost ? "status-drop-indicator-ghost" : "status-drop-indicator"}
                sx={{ my: 1, ml: 'auto' }}
              />
            )}

            <Accordion
              expanded={expandedStatuses.has(statusId)}
              onChange={() => toggleStatusExpansion(statusId)}
              sx={{ ...taskListStyles.accordion }}
            >
              <AccordionSummary
                ref={(el) => setAccordionSummaryRef(el, statusId)}
                expandIcon={<ExpandMore />}
                draggable={canReorderStatus}
                onDragStart={canReorderStatus ? (e) => handleStatusDragStart(e, status) : undefined}
                onDragEnd={canReorderStatus ? () => {
                  cleanupDragVisuals(dragOverStatusRef);
                  handleStatusDragEnd();
                } : undefined}
                onDragOver={(e) => {
                  statusDragHandlers.dragOver(e, statusId);
                  handleTicketDragOver(e, statusId);
                }}
                onDragLeave={(e) => {
                  statusDragHandlers.dragLeave(e);
                  handleTicketDragLeave(e);
                }}
                onDrop={(e) => {
                  e.stopPropagation(); // Prevent event bubbling to AccordionDetails
                  if (draggedStatus) {
                    statusDragHandlers.drop(e, statusId);
                  } else {
                    handleTicketDrop(e, statusId);
                  }
                }}
                data-accordion-summary={statusId}
                className={`${
                  // Only apply ticket drag classes when ticket is being dragged
                  (!draggedStatus && dragOverColumn === statusId && draggedTask) ? 'accordion-summary-ticket-drag-over' : ''
                  // Status drag visual feedback handled by DOM manipulation
                  } ${canReorderStatus ? 'status-header-draggable list-view' : 'status-header-disabled'
                  }`}
                sx={{
                  ...taskListStyles.accordionSummary,
                  ...(bulkModeColumnId === statusId && {
                    bgcolor: 'rgba(59, 130, 246, 0.05)',
                    borderColor: 'rgba(59, 130, 246, 0.2)'
                  })
                }}
              >
                <Box sx={taskListStyles.statusHeader}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1 }}>
                    {bulkModeColumnId === statusId && (
                      <>
                        <Tooltip title={selectedTasksByColumn[bulkModeColumnId]?.size === statusTasks.length ? t('dashboard.bulkActions.deselectAll') : (selectedTasksByColumn[bulkModeColumnId]?.size >= 100 ? t('dashboard.bulkActions.selectionLimitReached') : t('dashboard.bulkActions.selectAll'))} slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (selectedTasksByColumn[bulkModeColumnId]?.size === statusTasks.length) {
                                  onDeselectAllInColumn?.();
                                } else {
                                  onSelectAllInColumn?.();
                                }
                              }}
                              className="bulk-select-all-btn"
                              aria-label={selectedTasksByColumn[bulkModeColumnId]?.size === statusTasks.length ? t('dashboard.bulkActions.deselectAllInColumn') : t('dashboard.bulkActions.selectAllInColumn')}
                              role="checkbox"
                              aria-checked={selectedTasksByColumn[bulkModeColumnId]?.size === statusTasks.length}
                              disabled={selectedTasksByColumn[bulkModeColumnId]?.size >= 100 && selectedTasksByColumn[bulkModeColumnId]?.size !== statusTasks.length}
                            >
                              {selectedTasksByColumn[bulkModeColumnId]?.size === statusTasks.length ? (
                                <CheckBox className="bulk-checkbox-selected" />
                              ) : (
                                <CheckBoxOutlineBlank className="bulk-checkbox-unselected" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('dashboard.bulkActions.selectAllInfo')} arrow slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                          <InfoOutlined className="bulk-select-all-info-icon" onClick={(e) => e.stopPropagation()} />
                        </Tooltip>
                      </>
                    )}
                    <DragIndicator
                      sx={{
                        fontSize: 16,
                        color: canReorderStatus ? COLORS.gray[400] : COLORS.gray[300],
                        cursor: canReorderStatus ? 'grab' : 'default',
                        '&:hover': {
                          color: canReorderStatus ? COLORS.gray[600] : COLORS.gray[300]
                        }
                      }}
                    />
                    {getStatusIcon(status.name) && (
                      <Box component="img" src={getStatusIcon(status.name)} alt="" sx={{ width: 18, height: 18, flexShrink: 0 }} />
                    )}
                    <Typography variant="h6" sx={taskListStyles.statusTitle}>
                      {status.name}
                    </Typography>
                    <Chip
                      label={`${statusTasks.length}/${total}`}
                      size="small"
                      sx={taskListStyles.statusChip}
                    />
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <IconButton
                      size="small"
                      onClick={(e) => kebabMenu.handleOpen(e, statusId)}
                      data-menu-id={statusId}
                      sx={smallIconButtonStyles}
                      aria-label="More actions"
                      aria-haspopup="menu"
                    >
                      <MoreVert sx={{ fontSize: 16, color: COLORS.gray[500] }} />
                    </IconButton>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={taskListStyles.accordionDetails}
                onDragLeave={!draggedStatus ? handleTicketDragLeave : undefined}
                onDrop={!draggedStatus ? (e) => {
                  // Only handle drop if accordion is expanded (prevent duplicate with summary)
                  if (expandedStatuses.has(statusId)) {
                    handleTicketDrop(e, statusId);
                  }
                } : undefined}
              >
                <Box
                  ref={(el) => setStatusRef(el, statusId)}
                  data-status-id={statusId}
                  onDragOver={!draggedStatus ? (e) => handleTicketDragOver(e, statusId) : undefined}
                  sx={{
                    ...taskListStyles.scrollContainer,
                    ...(isBulkMode && {
                      pb: 4
                    }),
                    border: '2px solid transparent',
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&.list-drag-over-status': {
                      bgcolor: COLORS.blue[50],
                      border: `2px dashed ${COLORS.primary}`,
                      p: 1
                    }
                  }}
                >
                  <TableContainer component={Paper} elevation={0}>
                    <Table>
                      <TableHead>
                        <TableRow sx={tableHeaderStyles.row}>
                          <TableCell sx={{ ...tableHeaderStyles.cell, width: '25%' }}>{t('dashboard.task')}</TableCell>
                          <TableCell sx={{ ...tableHeaderStyles.cell, width: '10%' }}>{t('dashboard.code')}</TableCell>
                          <TableCell sx={{ ...tableHeaderStyles.cell, width: '12%' }}>{t('dashboard.priority')}</TableCell>
                          <TableCell sx={{ ...tableHeaderStyles.cell, width: '15%' }}>{t('dashboard.assignee')}</TableCell>
                          <TableCell sx={{ ...tableHeaderStyles.cell, width: '8%' }}>{t('dashboard.labels')}</TableCell>
                          <TableCell sx={{ ...tableHeaderStyles.cell, width: '10%' }}>{t('dashboard.estimateTime')}</TableCell>
                          <TableCell sx={{ ...tableHeaderStyles.cell, width: '10%' }}>{t('dashboard.dueDate')}</TableCell>
                          <TableCell sx={{ ...tableHeaderStyles.cell, width: '15%' }}>{t('dashboard.actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {statusTasks.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} sx={taskListStyles.noTasksCell}>
                              <Typography variant="body2" color="text.secondary">{t('dashboard.noTasksFound')}</Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          statusTasks.map(renderTaskRow)
                        )}
                        {columnPagination?.[statusId]?.loading && (
                          <TableRow>
                            <TableCell colSpan={8} sx={taskListStyles.loadingCell}>
                              <Typography variant="caption" sx={{ color: COLORS.gray[500] }}>{t('dashboard.loadingMore')}</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow sx={taskListStyles.spacerRow}>
                          <TableCell colSpan={8} sx={taskListStyles.spacerCell} />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </AccordionDetails>
            </Accordion>


          </React.Fragment>
        ))}

        {/* Bottom boundary indicator */}
        {draggedStatus && dragOverStatusRef.current?.position === 'bottom' && (
          <Box
            className={dragOverStatusRef.current?.isGhost ? "status-drop-indicator-ghost" : "status-drop-indicator"}
            sx={{ mt: 1, mb: 2, ml: 'auto' }}
          />
        )}

        {/* Drop indicator after last item */}
        {draggedStatus && dragOverStatusRef.current?.position === 'between' && dragOverStatusRef.current?.targetIndex === statusEntries.length && (
          <Box
            className={dragOverStatusRef.current?.isGhost ? "status-drop-indicator-ghost" : "status-drop-indicator"}
            sx={{ mt: 1, mb: 2, ml: 'auto' }}
          />
        )}
      </Box>

      {/* Kebab Menu */}
      <KebabMenu
        anchorEl={kebabMenu.anchorEl}
        open={kebabMenu.isOpen}
        onClose={kebabMenu.handleClose}
        menuItems={getKebabMenuItems}
        onItemClick={handleKebabMenuItemClick}
        disableClickAway={isBulkMode}
        scrollWithAnchor
      />

      {/* Task Action Menu */}
      <Menu
        anchorEl={taskMenuAnchorEl}
        open={Boolean(taskMenuAnchorEl)}
        onClose={handleTaskMenuClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            borderRadius: '10px',
            minWidth: 140,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            mt: 0.5
          }
        }}
      >
        <MenuItem
          onClick={(e) => {
            const task = activeMenuTask;
            handleTaskMenuClose(e);
            if (task) handleEdit(task);
          }}
          sx={{ gap: 1, fontSize: '12px', py: 0.4 }}
        >
          <Box component="img" src={ICONS.edit} sx={{ width: 14, height: 14 }} />
          {t('taskViewModal.editTask', 'Edit Task')}
        </MenuItem>
        <Divider />

        <MenuItem
          onClick={(e) => {
            const task = activeMenuTask;
            handleTaskMenuClose(e);
            if (task) copyTaskLink(task);
          }}
          sx={{ gap: 1, fontSize: '12px', py: 0.4 }}
        >
          <Box component="img" src={ICONS.copyLinkTicket} sx={{ width: 14, height: 14 }} />
          {t('taskViewModal.copyLink')}
        </MenuItem>
        <Divider />

        <MenuItem
          onClick={(e) => {
            const task = activeMenuTask;
            handleTaskMenuClose(e);
            if (task) copyTaskTitle(task);
          }}
          sx={{ gap: 1, fontSize: '12px', py: 0.4 }}
        >
          <ContentCopy sx={{ fontSize: 14, color: COLORS.gray[500] }} />
          {t('taskViewModal.copyTitle', 'Copy Title')}
        </MenuItem>
        <Divider />

        <MenuItem
          onClick={async (e) => {
            const task = activeMenuTask;
            handleTaskMenuClose(e);
            if (task) await handleTogglePin(task);
          }}
          sx={{ gap: 1, fontSize: '12px', py: 0.4 }}
        >
          <PushPin sx={{ fontSize: 14, color: activeMenuTask?.marked_as_pin ? COLORS.primary : COLORS.gray[500], transform: 'rotate(35deg)', flexShrink: 0 }} />
          {activeMenuTask?.marked_as_pin
            ? t('dashboard.unpinTicket', 'Unpin Ticket')
            : t('dashboard.pinTicket', 'Pin Ticket')}
        </MenuItem>
        <Divider />

        {(() => {
          const task = activeMenuTask;
          const linkCount = Object.values(task?.linked_tickets || {}).reduce((total, rel) => total + (rel.tickets?.length || 0), 0);
          const hasLinks = linkCount > 0;
          return (
            <>
              <MenuItem
                disabled={!hasLinks}
                onClick={(e) => {
                  handleTaskMenuClose(e);
                  if (task) onViewLinks?.(task);
                }}
                sx={{ gap: 1, fontSize: '12px', py: 0.4 }}
              >
                <Box component="img" src={ICONS.viewLink} sx={{ width: 14, height: 14, opacity: hasLinks ? 1 : 0.5 }} />
                {t('links.tooltips.viewLinks')}
              </MenuItem>
              <Divider />
            </>
          );
        })()}

        <MenuItem
          onClick={(e) => {
            const task = activeMenuTask;
            handleTaskMenuClose(e);
            if (task) onAddLink?.(task);
          }}
          sx={{ gap: 1, fontSize: '12px', py: 0.4 }}
        >
          <Box component="img" src={ICONS.attachFile} sx={{ width: 14, height: 14 }} />
          {t('links.tooltips.addLink')}
        </MenuItem>
        <Divider />

        {user?.role !== USER_ROLES.USER && (
          <MenuItem
            onClick={(e) => {
              const task = activeMenuTask;
              handleTaskMenuClose(e);
              if (task) handleDelete(task);
            }}
            sx={{
              gap: 1,
              fontSize: '12px',
              py: 0.4
            }}
          >
            <Box component="img" src={ICONS.delete} sx={{ width: 14, height: 14 }} />
            {t('dashboard.deleteTask', 'Delete Task')}
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default TaskListView;
